import { useState, useEffect, useCallback } from "react";
import { useAdminApi, toLocalDateStr, fmtMoney, exportXLSX } from "../../adminUtils";
import ExportBar from "./ExportBar";

export default function MerchReport() {
  const { API, AUTH } = useAdminApi();
  const today = toLocalDateStr(new Date());
  const [from, setFrom]       = useState(() => { const d = new Date(); d.setDate(1); return toLocalDateStr(d); });
  const [to, setTo]           = useState(today);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/merch/report?from=${from}&to=${to}`, { headers: AUTH });
      const json = await res.json();
      setData(json);
    } catch { setData(null); }
    finally   { setLoading(false); }
  }, [from, to, API, AUTH]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const doExport = () => {
  if (!data) return;
     const rows = Object.values(data.byProduct).map(p => ({
       Product: p.productName,
       "Units Sold": p.quantity,
       "Revenue": fmtMoney(p.revenue),
       "COGS": fmtMoney(p.cogs),
       "Tax": fmtMoney(p.tax),
       "Gross Profit": fmtMoney(p.profit),
       "Cash": fmtMoney(p.cash),
     }));
     rows.push({ Product: "TOTAL", "Units Sold": data.totals.unitsSold, "Revenue": fmtMoney(data.totals.grandRevenue), "COGS": fmtMoney(data.totals.grandCOGS), "Tax": fmtMoney(data.totals.grandTax), "Gross Profit": fmtMoney(data.totals.grandProfit) });
     const stockRows = data.products.map(p => ({ Product: p.name, Category: p.category, SKU: p.sku, Price: fmtMoney(p.price), Cost: fmtMoney(p.cost ?? 0), "Stock Remaining": p.stock, Taxable: p.taxable ? "Yes" : "No", Status: p.active ? "Active" : "Inactive" }));
     exportXLSX("Merch", [{ sheet: "Sales", rows }, { sheet: "Stock", rows: stockRows }], `florium-merch-${from}-${to}.xlsx`);
   };

  return (
    <div>
      <div className="adm-report-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label">From</label>
          <input type="date" className="adm-input" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">To</label>
          <input type="date" className="adm-input" value={to} max={today} onChange={e => setTo(e.target.value)} />
        </div>
      </div>

      {loading && <p className="adm-loading">Loading…</p>}
      {data && !loading && (
        <>
         <div className="adm-report-summary-row">
           <div className="adm-summary-chip adm-summary-chip--green">
             <span className="adm-summary-val">{fmtMoney(data.totals.grandRevenue)}</span>
             <span className="adm-summary-lbl">Revenue</span>
           </div>
           <div className="adm-summary-chip adm-summary-chip--green">
             <span className="adm-summary-val">{fmtMoney(data.totals.grandProfit)}</span>
             <span className="adm-summary-lbl">Gross profit</span>
           </div>
           <div className="adm-summary-chip adm-summary-chip--amber">
             <span className="adm-summary-val">{fmtMoney(data.totals.grandCOGS)}</span>
             <span className="adm-summary-lbl">COGS</span>
           </div>
           <div className="adm-summary-chip">
             <span className="adm-summary-val">{fmtMoney(data.totals.grandTax)}</span>
             <span className="adm-summary-lbl">Tax collected</span>
           </div>
           <div className="adm-summary-chip">
             <span className="adm-summary-val">{data.totals.unitsSold}</span>
             <span className="adm-summary-lbl">Units sold</span>
           </div>
           <div className="adm-summary-chip">
             <span className="adm-summary-val">{fmtMoney(data.totals.cashTotal)}</span>
             <span className="adm-summary-lbl">Cash</span>
           </div>
         </div>

          <ExportBar onXLSX={doExport} />

          <h4 className="adm-report-section-title">Sales by Product</h4>
          {Object.keys(data.byProduct).length === 0
            ? <p className="adm-empty">No sales in this period.</p>
            : (
             <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr><th>Product</th><th>Units</th><th>Revenue</th><th>COGS</th><th>Tax</th><th>Profit</th></tr>
                </thead>
                <tbody>
                  {Object.values(data.byProduct).map((p, i) => (
                    <tr key={i}>
                      <td>{p.productName}</td>
                      <td>{p.quantity}</td>
                      <td>{fmtMoney(p.revenue)}</td>
                      <td>{fmtMoney(p.cogs)}</td>
                      <td>{fmtMoney(p.tax)}</td>
                      <td><strong>{fmtMoney(p.profit)}</strong></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="adm-report-subtotal">
                    <td>Total</td>
                    <td>{data.totals.unitsSold}</td>
                    <td>{fmtMoney(data.totals.grandRevenue)}</td>
                    <td>{fmtMoney(data.totals.grandCOGS)}</td>
                    <td>{fmtMoney(data.totals.grandTax)}</td>
                    <td>{fmtMoney(data.totals.grandProfit)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            )
          }

          <h4 className="adm-report-section-title">Current Stock Levels</h4>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th></tr>
              </thead>
              <tbody>
                {data.products.map(p => (
                  <tr key={p._id}>
                    <td>{p.name}</td>
                    <td>{p.category || "—"}</td>
                    <td>{fmtMoney(p.price)}</td>
                    <td>
                      <span style={{ color: p.stock === 0 ? "#e53e3e" : p.stock <= 3 ? "#b97a0a" : "inherit", fontWeight: p.stock <= 3 ? 700 : 400 }}>
                        {p.stock}
                      </span>
                    </td>
                    <td><span className={`adm-status-badge ${p.active ? "status-confirmed" : "status-cancelled"}`}>{p.active ? "Active" : "Inactive"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}